import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ForkModuleError,
  useForkPublicModuleMutation,
  type PublicModule,
} from '@entities/PublicModule';
import { buildUnitPath } from '@shared/config';
import { ModuleAuthor } from './ModuleAuthor';
import './Library.css';

interface PublicModuleHeroProps {
  module: PublicModule;
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="M7 8l5-5 5 5" />
      <path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
    </svg>
  );
}

/** Шапка страницы публичного модуля: уровень, счётчики, автор, «Добавить себе» и «Поделиться». */
export function PublicModuleHero({ module: m }: PublicModuleHeroProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fork = useForkPublicModuleMutation();
  const [shareCopied, setShareCopied] = useState(false);
  const shareTimer = useRef<number>();
  // Локальный флаг «уже добавлено» — m.isAdded с бэка ИЛИ если бэк вернул
  // ALREADY_ADDED на попытку добавить (гонка/обновление).
  const [justAdded, setJustAdded] = useState(false);
  const added = m.isAdded || justAdded;

  useEffect(() => () => window.clearTimeout(shareTimer.current), []);

  const handleFork = () => {
    if (fork.isPending || added) return;
    fork.mutate(m.id, {
      onSuccess: (created) => navigate(buildUnitPath(created.id)),
      onError: (err) => {
        if (err instanceof ForkModuleError && err.code === 'ALREADY_ADDED') {
          setJustAdded(true);
        }
      },
    });
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareCopied(true);
      window.clearTimeout(shareTimer.current);
      shareTimer.current = window.setTimeout(() => setShareCopied(false), 2000);
    } catch {
      /* no clipboard available */
    }
  };

  return (
    <header className="lb-hero">
      <div>
        <div className="lb-hero-meta">
          {m.cefrLevel && <span className="lb-level-chip">{m.cefrLevel}</span>}
          <span>{t('library.cardCount', { count: m.cards.length })}</span>
          <span className="lb-dot" aria-hidden="true">·</span>
          <span>{t('library.installCount', { count: m.installs })}</span>
        </div>
        <h1 className="lb-hero-title">{m.name}</h1>
        {m.description && <p className="lb-hero-desc">{m.description}</p>}
        <ModuleAuthor
          name={m.author}
          official={m.isOfficial}
          role={t(m.isOfficial ? 'library.role.official' : 'library.role.community')}
        />
      </div>
      <div className="lb-hero-actions">
        <button
          type="button"
          className="lb-fork"
          data-added={added}
          aria-busy={fork.isPending}
          disabled={fork.isPending || added}
          onClick={handleFork}
        >
          {added ? t('library.added') : fork.isPending ? '…' : t('library.fork')}
        </button>
        <button type="button" className="lb-share" onClick={handleShare}>
          <ShareIcon />
          <span className="lb-share-label">{shareCopied ? t('library.shareDone') : t('library.share')}</span>
        </button>
      </div>
      {fork.isError && !added && <p className="lb-fork-error">{t('library.forkError')}</p>}
      <span className="lb-toast" role="status">{shareCopied ? t('library.shareDone') : ''}</span>
    </header>
  );
}
